import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

import { snapshotMinimizedLabelDom } from "./sidebarMinimizedLabelDebug";

interface SidebarContextValue {
  isMinimized: boolean;
  hoveredItemKey: string | null;
  setHoveredItemKey: (key: string | null) => void;
  openDropdowns: Record<string, boolean>;
  setOpenDropdowns: Dispatch<SetStateAction<Record<string, boolean>>>;
  compactStyle: string | null;
  location?: { pathname: string };
  auth?: {
    user?: {
      id?: number;
      userPermissions?: Array<{ permission: { name: string } }>;
    };
  };
}

const SidebarContext = createContext<SidebarContextValue | undefined>(
  undefined,
);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
  compactStyle?: string | null;
  location?: { pathname: string };
  auth?: {
    user?: {
      id?: number;
      userPermissions?: Array<{ permission: { name: string } }>;
    };
  };
}

export const SidebarProvider: FC<SidebarProviderProps> = ({
  children,
  compactStyle = null,
  location,
  auth,
}) => {
  const [isMinimized, setIsMinimized] = useState(
    document.body.classList.contains("sidebar-minimized"),
  );
  const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(
    null,
  );
  const [openDropdowns, setOpenDropdowns] = useState<
    Record<string, boolean>
  >({});

  // Monitor sidebar minimized state
  useEffect(() => {
    const checkMinimized = () => {
      const nowMinimized = document.body.classList.contains(
        "sidebar-minimized",
      );
      console.log("[SidebarContext] checkMinimized", {
        nowMinimized,
        bodyClasses: document.body.className,
      });
      setIsMinimized(nowMinimized);
    };

    // Check initial state
    checkMinimized();

    // Watch for class changes on body element
    const observer = new MutationObserver(checkMinimized);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Debug log when openDropdowns changes
  useEffect(() => {
    console.log("[SidebarContext] openDropdowns changed", openDropdowns);
  }, [openDropdowns]);

  // Debug log when isMinimized changes
  useEffect(() => {
    console.log("[SidebarContext] isMinimized changed", isMinimized);
  }, [isMinimized]);

  // Opt-in: localStorage sidebarMinimizedLabelDebug=1 — DOM snapshot after paint
  useEffect(() => {
    if (!isMinimized || typeof window === "undefined") {
      return;
    }
    let cancelled = false;
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!cancelled) {
          snapshotMinimizedLabelDom("after-isMinimized-true");
        }
      });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
    };
  }, [isMinimized]);

  return (
    <SidebarContext.Provider
      value={{
        isMinimized,
        hoveredItemKey,
        setHoveredItemKey,
        openDropdowns,
        setOpenDropdowns,
        compactStyle,
        location,
        auth,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
